import {
  BaseEdge,
  EdgeLabelRenderer,
  getBezierPath,
  useInternalNode,
  type EdgeProps,
} from "@xyflow/react";
import * as React from "react";
import { getEdgeParams } from "./floating";

/**
 * Edge whose endpoints float to the node border facing the other node, rather
 * than pinning to fixed top/bottom handles. Draws a bezier between the two
 * border intersection points and renders the condition label at its midpoint.
 */
export function FloatingEdge({
  id,
  source,
  target,
  markerEnd,
  style,
  label,
  selected,
  data,
}: EdgeProps) {
  const sourceNode = useInternalNode(source);
  const targetNode = useInternalNode(target);

  if (!sourceNode || !targetNode) {
    return null;
  }

  // An edge the run actually traversed: paint it charts-green with a heavier
  // stroke, matching FlowProgress's `.branch.taken`. Reuses an existing token —
  // no new palette. `data.taken` is set by FlowRunView.
  const taken = (data as { taken?: boolean } | undefined)?.taken === true;
  const edgeStyle: React.CSSProperties = taken
    ? {
        ...style,
        stroke: "var(--vscode-charts-green)",
        strokeWidth: 2.5,
      }
    : (style as React.CSSProperties);

  const { sx, sy, tx, ty, sourcePos, targetPos } = getEdgeParams(
    sourceNode,
    targetNode
  );

  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX: sx,
    sourceY: sy,
    sourcePosition: sourcePos,
    targetPosition: targetPos,
    targetX: tx,
    targetY: ty,
  });

  return (
    <>
      <BaseEdge id={id} path={edgePath} markerEnd={markerEnd} style={edgeStyle} />
      {label ? (
        <EdgeLabelRenderer>
          <div
            className={`fn-edge-label ${selected ? "selected" : ""}`}
            style={{
              position: "absolute",
              transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
              pointerEvents: "all",
            }}
          >
            {label}
          </div>
        </EdgeLabelRenderer>
      ) : null}
    </>
  );
}
